import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useTheme } from '@emotion/react';
import { makeStyles } from '@material-ui/core';
import { ReleaseNotePopupBox } from '@marqvision/shared';
import { useLogger } from '~/hooks';
import { Type } from './@types';
import { LOG_EVENT_NAME } from './@constants';
import { useViewModel } from './@hooks/useViewModel';
import { ReleaseNote } from './ReleaseNote';

const DELAY = 300; //ms

const MARGIN = {
  popover: {
    top: 5,
    right: 24,
  },
  arrow: {
    top: -2,
  },
};

const RELEASE_NOTE_MAX_SIZE = {
  width: 620,
  height: {
    list: 720,
    detail: 1024,
  },
};

const useStyles = makeStyles(({ color }) => ({
  wrapper: {
    position: 'fixed',
    zIndex: 1300, // Mui dialog z-index 와 맞춤
    color: color.content.neutral.default.normal,
    borderRadius: '8px',
    backgroundColor: color.bg.surface.neutral.default.normal,
    opacity: 0,
    overflow: 'hidden',
    transition: `${DELAY}ms`,
    boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.16), 0px 8px 16px 0px rgba(0, 0, 0, 0.20)',
    '&.isBorder': {
      border: `1px solid ${color.border.primary.default.normal}`,
      boxShadow: 'none',
    },
    '&.isOpen': {
      opacity: 1,
      animation: `$openEffect ${DELAY}ms`,
    },
  },
  '@keyframes openEffect': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  inner: {
    minWidth: '276px',
    minHeight: '107px',
    overflow: 'hidden',
    transition: `${DELAY}ms`,
  },
  resizeWrapper: {
    minWidth: '100%',
  },
  arrow: {
    position: 'fixed',
    zIndex: 1300, // Mui dialog z-index 와 맞춤
    width: '10px',
    height: '8px',
    transform: 'translateX(50%) translateZ(0)',
    display: 'grid',
    filter: 'drop-shadow(0px -2px 1px rgba(0, 0, 0, 0.06))',
  },
}));

const MARGIN_BOTTOM = 40;

type Props = {
  type: Type;
  anchor: HTMLDivElement;
  onExplore: () => void;
  onClose: () => void;
};

export const Wrapper = (props: Props) => {
  const { anchor, type, onExplore, onClose } = props;
  const classes = useStyles();
  const { color } = useTheme();

  const { amplitudeLogging } = useLogger();
  const { isPopupVisible, popupProps } = useViewModel(type === 'popup');

  const timeoutRef = useRef<NodeJS.Timeout>();
  const resizeObserver = useRef<ResizeObserver>();
  const resizeWrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [width, setWidth] = useState<number>(276);
  const [height, setHeight] = useState<string>();
  const [selectedId, setSelectedId] = useState<number>();

  const position = useMemo(() => {
    const viewport = window.innerWidth;
    const rect = anchor.getBoundingClientRect();
    return {
      popover: {
        right: MARGIN.popover.right,
        top: Math.floor(rect.top + rect.height + MARGIN.popover.top),
      },
      arrow: {
        right: Math.floor(viewport - rect.right + rect.width / 2),
        top: Math.floor(rect.top + rect.height + MARGIN.arrow.top),
      },
    };
  }, [anchor]);

  const getMaxHeight = useCallback(
    (height: number) => {
      const calcMaxHeight = `calc(100vh - ${position.popover.top}px - ${MARGIN_BOTTOM}px)`;
      return `min(${calcMaxHeight}, ${height}px)`;
    },
    [position.popover.top]
  );

  const innerWidth = type === 'release-note' ? `${RELEASE_NOTE_MAX_SIZE.width}px` : `${width}px`;
  const releaseNoteHeight = selectedId
    ? getMaxHeight(RELEASE_NOTE_MAX_SIZE.height.detail)
    : getMaxHeight(RELEASE_NOTE_MAX_SIZE.height.list);
  const innerHeight = type === 'release-note' ? releaseNoteHeight : `${height}px`;

  const handleClose = () => {
    setSelectedId(undefined);
    if (timeoutRef.current) return;
    setIsOpen(false);
    timeoutRef.current = setTimeout(() => {
      onClose();
      timeoutRef.current = undefined;
    }, DELAY);
  };

  const handlePopupClose = () => {
    amplitudeLogging(LOG_EVENT_NAME.closedPopup);
    handleClose();
  };

  useEffect(() => {
    const resizeWrapper = resizeWrapperRef.current;
    if (!resizeWrapper) return;

    const calculateHeight = (height: number) => {
      const newHeight = getMaxHeight(height);
      setHeight(newHeight);
    };

    resizeObserver.current = new ResizeObserver(([entry]) => {
      const height = entry.target.scrollHeight;
      calculateHeight(height);
    });
    resizeObserver.current.observe(resizeWrapper);

    return () => {
      resizeObserver.current?.unobserve(resizeWrapper);
    };
  }, [getMaxHeight]);

  return createPortal(
    <div
      className={clsx(classes.wrapper, { isOpen: isOpen && isPopupVisible, isBorder: type === 'popup' })}
      style={{ ...position.popover }}
    >
      <div className={classes.arrow} style={{ ...position.arrow }}>
        {type === 'popup' ? (
          <svg width="10" height="8" viewBox="0 0 40 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,32 L20,0 L40,32 Z" fill={color.border.primary.default.normal} />
            <path d="M6,32 L20,8 L34,32 Z" fill={color.bg.surface.neutral.default.normal} />
          </svg>
        ) : (
          <svg width="10" height="8" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
            <path d="M0,8 L5,0 L10,8 Z" fill={color.bg.surface.neutral.default.normal} />
          </svg>
        )}
      </div>
      <div className={classes.inner} style={{ width: innerWidth, height: innerHeight }}>
        <div ref={resizeWrapperRef} className={classes.resizeWrapper}>
          {type === 'popup' && popupProps && (
            <ReleaseNotePopupBox
              {...popupProps}
              onWidthChange={setWidth}
              onExplorer={onExplore}
              onClose={handlePopupClose}
            />
          )}
        </div>
        {type === 'release-note' && (
          <ReleaseNote selectedId={selectedId} onSelect={setSelectedId} onClose={handleClose} />
        )}
      </div>
    </div>,
    document.body
  );
};
